/**
 * 년주(年柱) 계산.
 *
 * 사주의 연도는 입춘(태양황경 315°, 양력 2월 4일경)을 기준으로 전환된다.
 * 출생일이 해당 연도의 입춘 이전이면 전년도를 사용한다.
 *
 * 공식: sexagenaryIndex = (유효년도 - 4) % 60
 *   stemIndex  = (유효년도 - 4) % 10
 *   branchIndex = (유효년도 - 4) % 12
 */

import type { Pillar } from '../types/pillars';
import { makePillar } from '../core/sexagenary';
import { dateToJD } from '../astronomy/julian-day';
import { getSolarTermsForYear } from '../astronomy/solar-terms';

/**
 * 년주를 계산한다.
 *
 * @param year - 양력 연도
 * @param month - 월 (1-12)
 * @param day - 일
 * @param hour - 시 (0-23)
 * @param minute - 분 (0-59)
 * @returns 년주
 */
export function getYearPillar(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): Pillar {
  // Find 입춘 time for this year
  const terms = getSolarTermsForYear(year);
  const ipchun = terms.find(t => t.name === '입춘');

  if (!ipchun) {
    throw new Error(`Cannot find 입춘 for year ${year}`);
  }

  const birthJD = dateToJD(year, month, day, hour, minute);

  // If birth is before 입춘, use previous year
  const effectiveYear = birthJD < ipchun.jd ? year - 1 : year;

  // Sexagenary cycle: year 4 CE = 甲子 (index 0)
  const sexagenaryIndex = ((effectiveYear - 4) % 60 + 60) % 60;
  return makePillar(sexagenaryIndex);
}
